export type HealthAlertLevel = 'high' | 'increasing' | 'stable';

export type HealthParameterKey =
    | 'bodyWounds'
    | 'snoutWounds'
    | 'scaleLoss'
    | 'skinBleeding'
    | 'finDamage'
    | 'emaciation'
    | 'deformity'
    | 'deformedJaw'
    | 'gillLid'
    | 'cataract'
    | 'eyeDamage'
    | 'maturity';

export interface HealthDateRange {
    from: string;
    to: string;
}

export interface HealthOverviewContext {
    asOf: string;
    daypartTag: string;
    weeklyWindow: HealthDateRange;
    trendWindow: HealthDateRange;
}

export interface HealthLastDayWelfare {
    numWounds: number | null;
    numHealingWounds: number | null;
    anyAcuteWound: number | null;
    noWound: number | null;
    anyWound: number | null;
    oneWound: number | null;
    moreThanOneWound: number | null;
    smallWound: number | null;
    mediumWound: number | null;
    bigWound: number | null;
    smallAcuteWound: number | null;
    mediumAcuteWound: number | null;
    bigAcuteWound: number | null;
    avgWoundSize: number | null;
    avgAcuteWoundSize: number | null;
    woundsArea: number | null;
    acuteWoundsArea: number | null;
    notSuperior: number | null;
    maturity: number | null;
    scaleLoss: number | null;
    startedMaturation: number | null;
    halfWayMature: number | null;
    fullyMature: number | null;
}

export interface HealthScoreMetrics {
    score0: number | null;
    score1: number | null;
    score2: number | null;
    score3: number | null;
    index: number | null;
    slope: number | null;
    count: number | null;
}

export interface HealthTrendPoint {
    day: string;
    score0: number | null;
    score1: number | null;
    score2: number | null;
    score3: number | null;
    index: number | null;
    /** Fish counted for this Laksvel parameter on that day (`laksvel_*_cnt`). */
    count: number | null;
}

export interface HealthParameterAlert {
    level: HealthAlertLevel;
    priority: number;
    appliedThreshold: number | null;
}

export interface HealthOverviewParameter {
    key: HealthParameterKey;
    daily: HealthScoreMetrics | null;
    weekly: HealthScoreMetrics | null;
    trend: HealthTrendPoint[];
    alert: HealthParameterAlert;
    /** Latest estimate is above the Laksvel image threshold: show Laksvel detections. */
    showImageViewer: boolean;
}

export interface HealthWoundTrendPoint {
    day: string;
    woundTypes: {
        numWounds: number | null;
        numHealingWounds: number | null;
    };
    sizeDistribution: {
        smallWound: number | null;
        mediumWound: number | null;
        bigWound: number | null;
    };
    distributionOfWounds: {
        moreThanOneWound: number | null;
        oneWound: number | null;
    };
    temperature: number | null;
    /** Fish counted for body wounds that day (`woundCount` / `laksvel_body_wounds_cnt`). */
    woundCount: number | null;
    notSuperior: number | null;
}

export interface HealthOverviewSummary {
    /** Fish counted for body wounds on the latest day (`woundCount` / `laksvel_body_wounds_cnt`). */
    woundCount: number | null;
    lastDayWelfare: HealthLastDayWelfare;
    /** Weekly `any_wound` trend (0–1), matching legacy welfare `woundSlope`. */
    woundSlope: number | null;
    parameters: HealthOverviewParameter[];
}

export interface HealthOverviewCharts {
    woundDailyTrend: HealthWoundTrendPoint[];
}

export interface HealthOverviewData {
    cageId: number;
    context: HealthOverviewContext;
    summary: HealthOverviewSummary;
    charts: HealthOverviewCharts;
}

export interface GetHealthOverviewParams {
    daypartTag?: 'day' | 'night' | 'all';
    from?: string;
    to?: string;
    fishGroupId?: number;
}

export type HealthDaypartTag = NonNullable<GetHealthOverviewParams['daypartTag']>;

/** Cage filter exposes `daypartTag` as string; coerce to API-allowed values. */
export function resolveHealthDaypartTag(daypartTag: string | undefined): HealthDaypartTag {
    if (daypartTag === 'night' || daypartTag === 'all') {
        return daypartTag;
    }
    return 'day';
}
